import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { cityNameValidation, companyNameValidation, invoicePrefixValidation, postalCodeValidation, streetNameValidation } from 'validators';
import { KYBProgressStatusEnum, KYBProgressStatusHelpers } from 'anchor-kyb-service/types';
import { Button, Callout, Card, Checkbox, Divider, MenuItem, Page, Select, Spinner, TextField, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { LogoPicker } from '~/components/LogoPicker/LogoPicker';
import { NavigationGuardModal } from '~/components/NavigationGuardModal';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { ownershipTypes, states } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useConnectBankAccount } from '~/hooks/useConnectBankAccount';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useStartKYC } from '~/hooks/useStartKYCPersona';
import { useUploadLogo } from '~/hooks/useUploadLogo';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handleRequest } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { isBankAccountConnected } from '~/store/store.model';
import { helpArticles } from '~/utils/helpArticles';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { BrandSettingsSection } from './BrandSettingsSection/BrandSettingsSection';
import styles from './BusinessDetails.module.scss';
const formId = 'business-details-form';
const testPrefix = 'settings-business-details';
export const BusinessDetailsTestIds = getTestIds('BusinessDetailsTestIds');
const ownershipTypeList = ownershipTypes.map((param)=>{
    let { value, display } = param;
    return {
        value,
        label: display
    };
});
const extraPrefixMaxLength = 8;
const defaultMaxPrefixLength = 4;
export function BusinessDetails() {
    const worksAt = useStore(selectors.user.business);
    const workspaces = useStore(selectors.user.workspaces);
    if (!workspaces) throw makeSentryIgnoredError('no workspaces');
    if (!worksAt) throw makeSentryIgnoredError('no business');
    return /*#__PURE__*/ _jsx(BusinessDetailsWithData, {
        worksAt: worksAt,
        workspaces: workspaces
    });
}
function BusinessDetailsWithData(param) {
    let { worksAt, workspaces } = param;
    var _ref;
    var _worksAt_settings_invoicePrefix, _worksAt_settings, _worksAt_settings1;
    const { t: texts } = useLocaleKeys('pages.businessDetails');
    const isVendor = useStore(selectors.user.isVendor);
    const payoutBankAccount = useStore(selectors.user.payoutBankAccount);
    const invoiceLineItemsGrouped = useStore(selectors.user.invoiceLineItemsGrouped);
    const invoiceLineItemDescriptionEnabled = useStore(selectors.user.invoiceLineItemDescriptionEnabled);
    const { api, setWorksAt, logBI, setWorkspaces } = useStoreActions();
    const { Description, ConnectButton, BankAccountDetails } = useConnectBankAccount();
    const { startKYC } = useStartKYC();
    const personaKYBStatus = useStore((s)=>{
        var _s_personaKYBStatus_data;
        return (_s_personaKYBStatus_data = s.personaKYBStatus.data) === null || _s_personaKYBStatus_data === void 0 ? void 0 : _s_personaKYBStatus_data.status;
    });
    const [grouped, setGrouped] = useState(invoiceLineItemsGrouped);
    const [showDescription, setShowDescription] = useState(invoiceLineItemDescriptionEnabled);
    const { showSuccessToast } = useOverlayContext();
    const { uploadLogo } = useUploadLogo(worksAt);
    const { BRAND_SETTINGS_BUSINESS_DETAILS } = useFeatureFlags();
    const [brandSettingsDirty, setBrandSettingsDirty] = useState(false);
    const maxInvoicePrefixLength = ((_ref = (_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : (_worksAt_settings_invoicePrefix = _worksAt_settings.invoicePrefix) === null || _worksAt_settings_invoicePrefix === void 0 ? void 0 : _worksAt_settings_invoicePrefix.length) !== null && _ref !== void 0 ? _ref : 0) > defaultMaxPrefixLength ? extraPrefixMaxLength : defaultMaxPrefixLength;
    useSetDocumentTitle(texts.title());
    const businessOwnership = useMemo(()=>ownershipTypeList.find((param)=>{
            let { value } = param;
            return value === worksAt.ownership;
        }) || null, [
        worksAt.ownership
    ]);
    const { control, formState, handleSubmit, watch, reset } = useForm({
        mode: 'all',
        defaultValues: getInitialFormState()
    });
    const invoicePrefix = watch('invoicePrefix');
    function getInitialFormState() {
        var _ref;
        var _worksAt_postalAddress, _worksAt_postalAddress1, _worksAt_postalAddress2, _worksAt_postalAddress3, _worksAt_postalAddress4, _worksAt_settings;
        return {
            businessName: worksAt.name,
            businessDescription: worksAt.description || '',
            address1: (_worksAt_postalAddress = worksAt.postalAddress) === null || _worksAt_postalAddress === void 0 ? void 0 : _worksAt_postalAddress.addressOne,
            address2: (_ref = (_worksAt_postalAddress1 = worksAt.postalAddress) === null || _worksAt_postalAddress1 === void 0 ? void 0 : _worksAt_postalAddress1.addressTwo) !== null && _ref !== void 0 ? _ref : '',
            city: (_worksAt_postalAddress2 = worksAt.postalAddress) === null || _worksAt_postalAddress2 === void 0 ? void 0 : _worksAt_postalAddress2.city,
            state: (_worksAt_postalAddress3 = worksAt.postalAddress) === null || _worksAt_postalAddress3 === void 0 ? void 0 : _worksAt_postalAddress3.state,
            postalCode: (_worksAt_postalAddress4 = worksAt.postalAddress) === null || _worksAt_postalAddress4 === void 0 ? void 0 : _worksAt_postalAddress4.postalCode,
            ownership: businessOwnership,
            logo: {
                asDataURL: worksAt.logoUrl,
                file: null
            },
            invoicePrefix: (_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : _worksAt_settings.invoicePrefix
        };
    }
    function resetFormState(updatedInvoicePrefix, updatedWorksAt) {
        var _ref;
        var _targetWorksAt_postalAddress, _targetWorksAt_postalAddress1, _targetWorksAt_postalAddress2, _targetWorksAt_postalAddress3, _targetWorksAt_postalAddress4, _targetWorksAt_settings;
        const targetWorksAt = updatedWorksAt !== null && updatedWorksAt !== void 0 ? updatedWorksAt : worksAt;
        reset({
            businessName: targetWorksAt.name,
            businessDescription: targetWorksAt.description || '',
            address1: (_targetWorksAt_postalAddress = targetWorksAt.postalAddress) === null || _targetWorksAt_postalAddress === void 0 ? void 0 : _targetWorksAt_postalAddress.addressOne,
            address2: (_ref = (_targetWorksAt_postalAddress1 = targetWorksAt.postalAddress) === null || _targetWorksAt_postalAddress1 === void 0 ? void 0 : _targetWorksAt_postalAddress1.addressTwo) !== null && _ref !== void 0 ? _ref : '',
            city: (_targetWorksAt_postalAddress2 = targetWorksAt.postalAddress) === null || _targetWorksAt_postalAddress2 === void 0 ? void 0 : _targetWorksAt_postalAddress2.city,
            state: (_targetWorksAt_postalAddress3 = targetWorksAt.postalAddress) === null || _targetWorksAt_postalAddress3 === void 0 ? void 0 : _targetWorksAt_postalAddress3.state,
            postalCode: (_targetWorksAt_postalAddress4 = targetWorksAt.postalAddress) === null || _targetWorksAt_postalAddress4 === void 0 ? void 0 : _targetWorksAt_postalAddress4.postalCode,
            ownership: ownershipTypeList.find((param)=>{
                let { value } = param;
                return value === targetWorksAt.ownership;
            }) || null,
            logo: {
                asDataURL: targetWorksAt.logoUrl,
                file: null
            },
            invoicePrefix: updatedInvoicePrefix !== null && updatedInvoicePrefix !== void 0 ? updatedInvoicePrefix : (_targetWorksAt_settings = targetWorksAt.settings) === null || _targetWorksAt_settings === void 0 ? void 0 : _targetWorksAt_settings.invoicePrefix
        });
    }
    async function onSubmit(param) {
        let { businessName, businessDescription, address1, address2, city, state, postalCode, ownership, logo } = param;
        var _initialFormState_ownership;
        logBI(10420);
        const params = {};
        const initialFormState = getInitialFormState();
        if (businessName !== initialFormState.businessName) params.name = businessName;
        if (businessDescription !== initialFormState.businessDescription) {
            params.description = businessDescription;
        }
        if (!BRAND_SETTINGS_BUSINESS_DETAILS && (logo === null || logo === void 0 ? void 0 : logo.file) && (logo === null || logo === void 0 ? void 0 : logo.asDataURL)) {
            uploadLogo({
                file: logo.file,
                asDataURL: logo.asDataURL
            });
            params.logoUrl = logo.asDataURL;
        }
        if (address1 !== initialFormState.address1 || address2 !== initialFormState.address2 || city !== initialFormState.city || state !== initialFormState.state || postalCode !== initialFormState.postalCode) {
            var _worksAt_postalAddress;
            params.postalAddress = {
                address1,
                address2: address2 || null,
                city,
                state,
                postalCode,
                country: ((_worksAt_postalAddress = worksAt.postalAddress) === null || _worksAt_postalAddress === void 0 ? void 0 : _worksAt_postalAddress.country) || 'US'
            };
        }
        if (ownership && ownership.value !== ((_initialFormState_ownership = initialFormState.ownership) === null || _initialFormState_ownership === void 0 ? void 0 : _initialFormState_ownership.value)) params.ownership = ownership.value;
        if (!Object.keys(params).length) return;
        const { success } = await handleRequest(api.vendor.updateBusinessDetails(worksAt.id, params));
        if (success) {
            var _ref, _logo_asDataURL;
            var _worksAt_postalAddress1;
            const { name, description, ownership, postalAddress } = params;
            const shouldChangeWorkspace = !!name;
            const updatedBusinessData = _object_spread_props(_object_spread({}, worksAt), {
                name: name !== null && name !== void 0 ? name : worksAt.name,
                description: description !== undefined ? description : worksAt.description,
                ownership: ownership !== null && ownership !== void 0 ? ownership : worksAt.ownership,
                postalAddress: postalAddress ? {
                    addressOne: postalAddress.address1,
                    addressTwo: postalAddress.address2,
                    city: postalAddress.city,
                    state: postalAddress.state,
                    postalCode: postalAddress.postalCode,
                    country: (_ref = (_worksAt_postalAddress1 = worksAt.postalAddress) === null || _worksAt_postalAddress1 === void 0 ? void 0 : _worksAt_postalAddress1.country) !== null && _ref !== void 0 ? _ref : 'US'
                } : worksAt.postalAddress,
                logoUrl: (_logo_asDataURL = logo.asDataURL) !== null && _logo_asDataURL !== void 0 ? _logo_asDataURL : worksAt.logoUrl
            });
            setWorksAt(updatedBusinessData);
            if (shouldChangeWorkspace) {
                const newWorkspaces = workspaces.map((workspace)=>workspace.id() === worksAt.id ? workspace.setName(name) : workspace);
                setWorkspaces(newWorkspaces);
            }
            resetFormState(undefined, updatedBusinessData);
        }
    }
    async function handleSaveBillingSettings() {
        var _worksAt_settings, _worksAt_settings1;
        if (grouped === invoiceLineItemsGrouped && showDescription === invoiceLineItemDescriptionEnabled && invoicePrefix === ((_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : _worksAt_settings.invoicePrefix) || !worksAt.settings || !!formState.errors.invoicePrefix) return;
        const invoiceLineItems = grouped ? 'grouped' : 'detailed';
        const invoiceLineItemDescription = showDescription ? 'service' : 'none';
        const { success } = await handleRequest(api.vendor.updateBusinessSettings(worksAt.id, _object_spread_props(_object_spread({}, worksAt.settings), {
            invoiceLineItems,
            invoiceLineItemDescription,
            coverCreditCardFees: ((_worksAt_settings1 = worksAt.settings) === null || _worksAt_settings1 === void 0 ? void 0 : _worksAt_settings1.feeControls.coverCreditCardFees) || false,
            invoicePrefix
        })));
        if (success) {
            showSuccessToast(texts.billingSettings.savedSuccessfully(), 'billing-settings-success-update-toast');
            const updatedWorksAt = _object_spread_props(_object_spread({}, worksAt), {
                settings: worksAt.settings && _object_spread_props(_object_spread({}, worksAt.settings), {
                    invoiceLineItems,
                    invoiceLineItemDescription,
                    invoicePrefix
                })
            });
            setWorksAt(updatedWorksAt);
            resetFormState(invoicePrefix, updatedWorksAt);
        }
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs(Page, {
            header: texts.title(),
            testId: testPrefix + '-screen',
            skin: "secondary",
            children: [
                /*#__PURE__*/ _jsxs("form", {
                    id: formId,
                    onSubmit: handleSubmit(onSubmit),
                    children: [
                        /*#__PURE__*/ _jsxs(Card, {
                            bi: {
                                name: 'business_details_card'
                            },
                            className: styles.fieldsLayout,
                            autoHeight: true,
                            padding: "m",
                            disableSeparator: true,
                            title: texts.details.title(),
                            children: [
                                /*#__PURE__*/ _jsx(Divider, {
                                    margin: 8
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: styles.detailsContainer,
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: styles.detailsLeftContainer,
                                            children: [
                                                /*#__PURE__*/ _jsx(Controller, {
                                                    control: control,
                                                    name: "businessName",
                                                    rules: companyNameValidation('Business name', {
                                                        required: texts.details.businessNameRequired()
                                                    }),
                                                    render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                            collapseHelperText: false
                                                        }, connectControllerToTextField(p)), {
                                                            label: texts.details.businessName(),
                                                            testId: testPrefix + '-business-name',
                                                            bi: {
                                                                name: 'business_name'
                                                            }
                                                        }))
                                                }),
                                                /*#__PURE__*/ _jsx(Controller, {
                                                    control: control,
                                                    name: "businessDescription",
                                                    render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                            collapseHelperText: true,
                                                            showHelperTextOnFocusOnly: true
                                                        }, connectControllerToTextField(p)), {
                                                            label: texts.details.businessDescription(),
                                                            helperText: texts.details.businessDescriptionHelper(),
                                                            testId: BusinessDetailsTestIds.BusinessDescription(),
                                                            bi: {
                                                                name: 'business_description'
                                                            }
                                                        }))
                                                }),
                                                isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Controller, {
                                                            control: control,
                                                            name: "address1",
                                                            rules: streetNameValidation(undefined, {
                                                                required: texts.details.streetAddressRequired()
                                                            }),
                                                            render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                                    collapseHelperText: false
                                                                }, connectControllerToTextField(p)), {
                                                                    label: texts.details.address1(),
                                                                    testId: testPrefix + '-address1',
                                                                    bi: {
                                                                        name: 'address_1'
                                                                    }
                                                                }))
                                                        }),
                                                        /*#__PURE__*/ _jsx(Controller, {
                                                            control: control,
                                                            name: "address2",
                                                            rules: streetNameValidation(),
                                                            render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                                    collapseHelperText: false
                                                                }, connectControllerToTextField(p)), {
                                                                    label: texts.details.address2(),
                                                                    testId: testPrefix + '-address2',
                                                                    bi: {
                                                                        name: 'address_2'
                                                                    }
                                                                }))
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: styles.address,
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Controller, {
                                                                    control: control,
                                                                    name: "postalCode",
                                                                    rules: postalCodeValidation(undefined, {
                                                                        required: texts.details.postalCodeRequired()
                                                                    }),
                                                                    render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                                            collapseHelperText: false
                                                                        }, connectControllerToTextField(p)), {
                                                                            label: texts.details.postalCode(),
                                                                            testId: testPrefix + '-postal-code',
                                                                            bi: {
                                                                                name: 'postal_code'
                                                                            }
                                                                        }))
                                                                }),
                                                                /*#__PURE__*/ _jsx(Controller, {
                                                                    control: control,
                                                                    name: "city",
                                                                    rules: cityNameValidation(undefined, {
                                                                        required: texts.details.cityRequired()
                                                                    }),
                                                                    render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                                            collapseHelperText: false
                                                                        }, connectControllerToTextField(p)), {
                                                                            label: texts.details.city(),
                                                                            testId: testPrefix + '-city',
                                                                            bi: {
                                                                                name: 'city'
                                                                            }
                                                                        }))
                                                                }),
                                                                /*#__PURE__*/ _jsx(Controller, {
                                                                    control: control,
                                                                    name: "state",
                                                                    rules: {
                                                                        required: true
                                                                    },
                                                                    render: (p)=>/*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                                                                            collapseHelperText: false,
                                                                            label: texts.details.state(),
                                                                            menuMaxHeight: 300
                                                                        }, connectControllerToTextField(p)), {
                                                                            testId: testPrefix + '-dropdown-state',
                                                                            bi: {
                                                                                name: 'state'
                                                                            },
                                                                            children: states.map((state)=>/*#__PURE__*/ _jsx(MenuItem, {
                                                                                    value: state,
                                                                                    children: state
                                                                                }, state))
                                                                        }))
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        isVendor && !BRAND_SETTINGS_BUSINESS_DETAILS && /*#__PURE__*/ _jsx("div", {
                                            className: styles.detailsRightContainer,
                                            children: /*#__PURE__*/ _jsx(Controller, {
                                                control: control,
                                                name: "logo",
                                                render: (img)=>{
                                                    var _img_field_value_asDataURL;
                                                    return /*#__PURE__*/ _jsx(LogoPicker, {
                                                        isNewUI: true,
                                                        image: (_img_field_value_asDataURL = img.field.value.asDataURL) !== null && _img_field_value_asDataURL !== void 0 ? _img_field_value_asDataURL : '',
                                                        onChange: img.field.onChange,
                                                        testId: testPrefix + '-logo',
                                                        showEditButton: true,
                                                        editButtonLabel: texts.brandSettings.changeLogo()
                                                    });
                                                }
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    intent: Intent.UpdateBusinessDetails,
                                    type: "submit",
                                    form: formId,
                                    disabled: !formState.isValid,
                                    loading: formState.isSubmitting,
                                    testId: testPrefix + '-btn-save-changes',
                                    bi: {
                                        name: 'save_changes'
                                    },
                                    children: texts.saveChanges()
                                })
                            ]
                        }),
                        isVendor && BRAND_SETTINGS_BUSINESS_DETAILS && /*#__PURE__*/ _jsx(BrandSettingsSection, {
                            worksAt: worksAt,
                            onDirtyChange: setBrandSettingsDirty
                        }),
                        personaKYBStatus && isVendor && /*#__PURE__*/ _jsxs(Card, {
                            bi: {
                                name: 'business_tax_information_card'
                            },
                            className: styles.fieldsLayout,
                            autoHeight: true,
                            disableSeparator: true,
                            padding: "m",
                            title: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "h5",
                                        weight: "normal",
                                        testId: "tax-information-title",
                                        children: texts.taxInformation.title()
                                    }),
                                    /*#__PURE__*/ _jsxs(Typography, {
                                        variant: "paragraph1",
                                        children: [
                                            texts.taxInformation.description(),
                                            /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                                href: helpArticles.kFormIssuance2024(),
                                                testId: 'tax-information-read-more',
                                                underline: "none",
                                                openInNewTab: true,
                                                bi: {
                                                    name: 'tax_information_read_more'
                                                },
                                                children: texts.taxInformation.learnMore()
                                            })
                                        ]
                                    })
                                ]
                            }),
                            children: [
                                /*#__PURE__*/ _jsx(Divider, {
                                    margin: 0
                                }),
                                [
                                    KYBProgressStatusEnum.NotStarted,
                                    KYBProgressStatusEnum.DetailsNeeded
                                ].includes(personaKYBStatus) && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            children: texts.taxInformation.kybNotStarted()
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            bi: {
                                                name: 'start_kyb_verification'
                                            },
                                            onClick: startKYC,
                                            testId: "complete-kyb-button",
                                            children: texts.taxInformation.completeKyb()
                                        })
                                    ]
                                }),
                                (KYBProgressStatusHelpers.isAwaitingDecision(personaKYBStatus) || personaKYBStatus === KYBProgressStatusEnum.Declined) && /*#__PURE__*/ _jsxs("div", {
                                    "data-testid": "awaiting-kyb",
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            children: texts.taxInformation.kybPending()
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            skin: "disabled",
                                            children: texts.taxInformation.kybProcessingTime()
                                        })
                                    ]
                                }),
                                personaKYBStatus === KYBProgressStatusEnum.Approved && /*#__PURE__*/ _jsx(RestfulWrapper, {
                                    fetch: ()=>api.vendor.getTaxInfo(worksAt.id),
                                    renderLoader: ()=>/*#__PURE__*/ _jsx(Spinner, {
                                            centeredInBox: true,
                                            centerBoxWidth: "100%",
                                            testId: "tax-info-loader"
                                        }),
                                    renderChild: (param)=>{
                                        let { data } = param;
                                        return /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(TextField, {
                                                    bi: {
                                                        name: 'business_legal_name'
                                                    },
                                                    collapseHelperText: false,
                                                    label: texts.taxInformation.businessLegalName(),
                                                    value: data.businessLegalName,
                                                    disabled: true,
                                                    testId: "business-legal-name"
                                                }),
                                                /*#__PURE__*/ _jsx(TextField, {
                                                    bi: {
                                                        name: 'business_registered_address'
                                                    },
                                                    collapseHelperText: false,
                                                    label: texts.taxInformation.registeredAddress(),
                                                    value: data.registeredBusinessAddress,
                                                    disabled: true,
                                                    testId: "registered-business-address"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: styles.taxIdContainer,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(TextField, {
                                                            bi: {
                                                                name: 'business_tax_id'
                                                            },
                                                            collapseHelperText: false,
                                                            style: {
                                                                flex: '3'
                                                            },
                                                            label: texts.taxInformation.taxId(),
                                                            value: data.businessTaxId,
                                                            disabled: true,
                                                            testId: "business-tax-id"
                                                        }),
                                                        /*#__PURE__*/ _jsx(TextField, {
                                                            bi: {
                                                                name: 'business_tax_id_type'
                                                            },
                                                            collapseHelperText: false,
                                                            style: {
                                                                flex: '1'
                                                            },
                                                            label: texts.taxInformation.taxIdType(),
                                                            value: data.businessTaxIdType.toUpperCase(),
                                                            disabled: true,
                                                            testId: "business-tax-id-type"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(TextField, {
                                                    bi: {
                                                        name: 'merchant_category_code'
                                                    },
                                                    collapseHelperText: false,
                                                    label: texts.taxInformation.merchantCategoryCode(),
                                                    value: data.merchantCategoryCode,
                                                    disabled: true,
                                                    testId: "merchant-category-code"
                                                })
                                            ]
                                        });
                                    }
                                })
                            ]
                        }),
                        isVendor && /*#__PURE__*/ _jsxs(Card, {
                            bi: {
                                name: 'business_billing_settings_card'
                            },
                            className: styles.fieldsLayout,
                            autoHeight: true,
                            padding: "m",
                            disableSeparator: true,
                            title: texts.billingSettings.title(),
                            children: [
                                /*#__PURE__*/ _jsx(Divider, {
                                    margin: 0
                                }),
                                /*#__PURE__*/ _jsx(Checkbox, {
                                    checked: grouped,
                                    onChange: (e)=>{
                                        setGrouped(e.currentTarget.checked);
                                    },
                                    testId: "group-invoice-line-items",
                                    bi: {
                                        name: 'group_invoice_line_items'
                                    },
                                    children: texts.billingSettings.groupLineItems()
                                }),
                                /*#__PURE__*/ _jsx(Checkbox, {
                                    checked: showDescription,
                                    onChange: (e)=>{
                                        setShowDescription(e.currentTarget.checked);
                                    },
                                    testId: "show-line-item-description",
                                    bi: {
                                        name: 'show_line_item_description'
                                    },
                                    children: texts.billingSettings.showLineItemDescription()
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: styles.prefixContainer,
                                    children: [
                                        /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "invoicePrefix",
                                            rules: invoicePrefixValidation('Invoice prefix', {
                                                required: texts.billingSettings.invoicePrefixRequired(),
                                                maxLimit: maxInvoicePrefixLength
                                            }),
                                            render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                    collapseHelperText: false
                                                }, connectControllerToTextField(p)), {
                                                    label: texts.billingSettings.invoicesPrefix(),
                                                    testId: "invoice-prefix",
                                                    width: "220px",
                                                    bi: {
                                                        name: 'invoice_prefix'
                                                    },
                                                    onChange: (e)=>{
                                                        p.field.onChange(e.target.value.toUpperCase());
                                                    }
                                                }))
                                        }),
                                        invoicePrefix !== ((_worksAt_settings1 = worksAt.settings) === null || _worksAt_settings1 === void 0 ? void 0 : _worksAt_settings1.invoicePrefix) && /*#__PURE__*/ _jsx(Callout, {
                                            intent: "info",
                                            testId: "prefix-changes-info-callout",
                                            className: styles.callout,
                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                children: texts.billingSettings.prefixCallout()
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    testId: "save-billing-changes",
                                    onClick: handleSaveBillingSettings,
                                    bi: {
                                        name: 'save_billing_settings'
                                    },
                                    children: texts.saveChanges()
                                })
                            ]
                        }),
                        isVendor && /*#__PURE__*/ _jsxs(Card, {
                            bi: {
                                name: 'business_bank_account_card'
                            },
                            className: styles.fieldsLayout,
                            autoHeight: true,
                            padding: "m",
                            disableSeparator: true,
                            title: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        weight: "normal",
                                        variant: "h5",
                                        children: texts.bankAccount.title()
                                    }),
                                    /*#__PURE__*/ _jsx(Description, {
                                        testId: "bank-account-details-title",
                                        linkTestId: "bank-account-details-read-more"
                                    })
                                ]
                            }),
                            testId: 'bank-account-details',
                            children: [
                                /*#__PURE__*/ _jsx(Divider, {
                                    margin: 0
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: styles.bankDetails,
                                    children: [
                                        isBankAccountConnected(payoutBankAccount) ? /*#__PURE__*/ _jsx(BankAccountDetails, {
                                            payoutBankAccount: payoutBankAccount
                                        }) : /*#__PURE__*/ _jsx(Typography, {
                                            children: texts.bankAccount.noBankAccount()
                                        }),
                                        /*#__PURE__*/ _jsx(ConnectButton, {
                                            className: styles.bankActions,
                                            enterManuallyTestId: "enter-manually",
                                            testId: "connect-bank-account"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(NavigationGuardModal, {
                    when: Object.keys(formState.dirtyFields).length > 0 || brandSettingsDirty,
                    primaryButtonTestId: "business-details-confirm-navigation",
                    secondaryButtonTestId: "business-details-keep-editing"
                })
            ]
        })
    });
}
